import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { store } from "store";

import AppLayout         from "components/layout/AppLayout";
import RequireAuth       from "components/common/RequireAuth";
import LoginPage          from "features/auth/LoginPage";
import ResetPasswordPage   from "features/auth/ResetPasswordPage";
import ForgotPasswordPage  from "features/auth/ForgotPasswordPage";
import VerifyAccountPage  from "features/auth/VerifyAccountPage";

import HomePage               from "features/scheduleItems/HomePage";
import ChangeRequestsPage     from "features/scheduleItems/ChangeRequestsPage";
import ScheduleItemDetailPage from "features/scheduleItems/ScheduleItemDetailPage";
import { PartnersPage, PartnerDetailPage } from "features/partners/PartnersPage";
import { LogsPage, LogDetailPage }         from "features/logs/LogsPage";
import { UsersPage, UserDetailPage }       from "features/users/UsersPage";
import AddNewPage            from "features/partners/AddNewPage";
import BlackoutDaysPage     from "features/partners/BlackoutDaysPage";
import ToolsPage                   from "features/tools/ToolsPage";
import MailerToolsPage              from "features/tools/MailerToolsPage";
import CourierCalendarPage          from "features/tools/CourierCalendarPage";
import ImportPartnersPage          from "features/tools/ImportPartnersPage";
import ImportPartnersSummaryPage   from "features/tools/ImportPartnersSummaryPage";
import PartnerHomePage, { PartnerAllPickupsPage } from "features/partner/PartnerHomePage";
import { PartnerInfoPage, PartnerHistoryPage }   from "features/partner/PartnerPages";

import AccountPage          from "features/account/AccountPage";
import NewPartnerForm       from "features/forms/NewPartnerForm";
import NewLogForm           from "features/forms/NewLogForm";
import EditLogForm          from "features/forms/EditLogForm";
import NewUserForm          from "features/forms/NewUserForm";
import NewScheduleItemForm  from "features/forms/NewScheduleItemForm";
import ImportScheduleWizard from "features/forms/ImportScheduleWizard";

function RootPage() {
  const { isPartner } = useAuth();
  return isPartner ? <PartnerHomePage /> : <HomePage />;
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 2, retry: 1 } },
});

// comment to trigger deploy
export default function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/login"                   element={<LoginPage />} />
            <Route path="/forgot-password"          element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token"   element={<ResetPasswordPage />} />
            <Route path="/verify-account/:token"   element={<VerifyAccountPage />} />

            {/* Protected */}
            <Route element={<RequireAuth />}>
              <Route element={<AppLayout />}>

                {/* All authenticated roles */}
                <Route path="/"        element={<RootPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/schedule-items/:id" element={<ScheduleItemDetailPage />} />


                {/* Admin + Courier */}
                <Route element={<RequireAuth allowedRoles={["bridge13_admin", "bridge13_courier"]} />}>
                  <Route path="/schedule-items/:id" element={<ScheduleItemDetailPage />} />
                  <Route path="/partners"                       element={<PartnersPage />} />
                  <Route path="/partners/:id"                   element={<PartnerDetailPage />} />
                  <Route path="/partners/:id/blackout-days"     element={<BlackoutDaysPage />} />
                  <Route path="/logs"               element={<LogsPage />} />
                  <Route path="/logs/:id"           element={<LogDetailPage />} />
                  <Route path="/logs/:id/edit"      element={<EditLogForm />} />
                  <Route path="/logs/new"           element={<NewLogForm />} />
                </Route>

                {/* Admin only */}
                <Route element={<RequireAuth allowedRoles={["bridge13_admin"]} />}>
                  <Route path="/change-requests"        element={<ChangeRequestsPage />} />
                  <Route path="/add-new"                element={<AddNewPage />} />
                  <Route path="/partners/new"           element={<NewPartnerForm />} />
                  <Route path="/users"                  element={<UsersPage />} />
                  <Route path="/users/:id"              element={<UserDetailPage />} />
                  <Route path="/users/new"              element={<NewUserForm />} />
                  <Route path="/schedule-items/new"     element={<NewScheduleItemForm />} />
                  <Route path="/schedule-import"        element={<ImportScheduleWizard />} />
                  <Route path="/tools"                             element={<ToolsPage />} />
                  <Route path="/tools/mailer-tools"               element={<MailerToolsPage />} />
                  <Route path="/tools/courier-calendar"           element={<CourierCalendarPage />} />
                  <Route path="/tools/import-partners"            element={<ImportPartnersPage />} />
                  <Route path="/tools/import-partners/summary"   element={<ImportPartnersSummaryPage />} />
                </Route>

                {/* Partner role */}
                <Route element={<RequireAuth allowedRoles={["partner"]} />}>
                  <Route path="/pickups"            element={<PartnerAllPickupsPage />} />
                  <Route path="/info"               element={<PartnerInfoPage />} />
                  <Route path="/history"            element={<PartnerHistoryPage />} />
                  <Route path="/history/:id"        element={<LogDetailPage />} />
                </Route>

              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Provider>
  );
}
